import { useState, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Loader2, TrendingUp, Users, FileText, Wallet, ArrowLeft, ChevronRight, Trash2, CheckCircle, Wrench } from 'lucide-react'
import { toast } from 'sonner'
import { api } from '@/lib/axios'

interface DashboardKPI {
  total_verified_profit_usd: number
  total_verified_profit_eur: number
  total_platform_fees_eur: number
  total_broken_props: number
  total_phase_transitions: number
  total_payouts_received: number
  total_payouts_amount_usd: number
  active_prop_accounts: number
  users_with_profits: number
  unpaid_invoices_count: number
  cycle_start: string
  cycle_end: string
}

interface UserSummary {
  user_id: number
  email: string
  active_props_count: number
  verified_profit_usd: number
  verified_profit_eur: number
  platform_fee_eur: number
  payment_status: string
  last_snapshot_date: string | null
}

interface SnapshotData {
  id: number
  snapshot_date: string
  user_id: number
  email: string
  prop_account_id: number
  prop_account_name: string
  provider: string
  prop_size: number
  prop_equity: number
  prop_equity_percentage: number
  broker_balance: number
  total_payouts: number
  payout_count: number
  current_phase: string
  is_prop_broken: boolean
  phase_transition_detected: string | null
}

interface PayoutData {
  id: number
  payout_date: string
  user_id: number
  email: string
  prop_account_id: number
  prop_account_name: string
  payout_amount: number
  payout_number: number
  is_first_payout: boolean
  cost_refunded: boolean
  metaapi_deal_id: string | null
}

interface TradeData {
  id: number
  transaction_id: string
  symbol: string
  direction: string
  volume: number
  open_price: number
  close_price: number | null
  stop_loss: number | null
  take_profit: number | null
  status: string
  profit_loss: number
  broker_profit_loss: number | null
  commission: number
  swap: number
  opened_at: string
  closed_at: string | null
  prop_account_id: number | null
  prop_account_name: string | null
  broker_account_id: number | null
  broker_account_name: string | null
  is_prop_trade: boolean
}

interface HedgePairTrades {
  hedge_pair_id: number
  display_name: string | null
  prop_account_id: number
  prop_account_name: string
  broker_account_id: number
  broker_account_name: string
  trades: TradeData[]
  total_profit_loss: number
  total_prop_pnl: number
  total_broker_pnl: number
  trades_count: number
}

interface UserTradesData {
  user_id: number
  email: string
  hedge_pairs: HedgePairTrades[]
  total_trades: number
  total_profit_loss: number
  total_prop_pnl: number
  total_broker_pnl: number
}

export const ProfitsVerification = () => {
  const [activeTab, setActiveTab] = useState<'dashboard' | 'users' | 'snapshots' | 'payouts'>('dashboard')
  const [loading, setLoading] = useState(false)
  
  // Dashboard data
  const [kpi, setKpi] = useState<DashboardKPI | null>(null)
  
  // Users data
  const [users, setUsers] = useState<UserSummary[]>([])
  
  // Snapshots data
  const [snapshots, setSnapshots] = useState<SnapshotData[]>([])
  const [onlyBroken, setOnlyBroken] = useState(false)
  
  // Payouts data
  const [payouts, setPayouts] = useState<PayoutData[]>([])
  
  // User trades detail
  const [selectedUser, setSelectedUser] = useState<UserSummary | null>(null)
  const [userTrades, setUserTrades] = useState<UserTradesData | null>(null)
  const [loadingTrades, setLoadingTrades] = useState(false)
  
  // Trade management
  const [selectedTradeIds, setSelectedTradeIds] = useState<Set<number>>(new Set())
  const [processingTrades, setProcessingTrades] = useState(false)

  useEffect(() => {
    // Reset user detail view when changing tabs
    if (activeTab !== 'users') {
      setSelectedUser(null)
      setUserTrades(null)
    }
    
    if (activeTab === 'dashboard') loadDashboard()
    if (activeTab === 'users') loadUsers()
    if (activeTab === 'snapshots') loadSnapshots()
    if (activeTab === 'payouts') loadPayouts()
  }, [activeTab, onlyBroken])

  const loadDashboard = async () => {
    setLoading(true)
    try {
      const { data } = await api.get('/admin/profits/dashboard')
      setKpi(data)
    } catch (err: any) {
      toast.error('Errore caricamento dashboard: ' + (err.response?.data?.detail || err.message))
    } finally {
      setLoading(false)
    }
  }

  const loadUsers = async () => {
    setLoading(true)
    try {
      const { data } = await api.get('/admin/profits/users-summary')
      setUsers(data)
    } catch (err: any) {
      toast.error('Errore caricamento utenti: ' + (err.response?.data?.detail || err.message))
    } finally {
      setLoading(false)
    }
  }

  const loadSnapshots = async () => {
    setLoading(true)
    try {
      const params = onlyBroken ? { only_broken: true } : {}
      const { data } = await api.get('/admin/profits/all-snapshots', { params })
      setSnapshots(data)
    } catch (err: any) {
      toast.error('Errore caricamento snapshots: ' + (err.response?.data?.detail || err.message))
    } finally {
      setLoading(false)
    }
  }

  const loadPayouts = async () => {
    setLoading(true)
    try {
      const { data } = await api.get('/admin/profits/all-payouts')
      setPayouts(data)
    } catch (err: any) {
      toast.error('Errore caricamento payouts: ' + (err.response?.data?.detail || err.message))
    } finally {
      setLoading(false)
    }
  }

  const loadUserTrades = async (userId: number) => {
    setLoadingTrades(true)
    try {
      const { data } = await api.get(`/admin/users/${userId}/trades`)
      setUserTrades(data)
    } catch (err: any) {
      toast.error('Errore caricamento operazioni: ' + (err.response?.data?.detail || err.message))
    } finally {
      setLoadingTrades(false)
    }
  }

  const handleUserClick = (user: UserSummary) => {
    setSelectedUser(user)
    loadUserTrades(user.user_id)
  }

  const handleBackToUsers = () => {
    setSelectedUser(null)
    setUserTrades(null)
    setSelectedTradeIds(new Set())
  }

  const toggleTradeSelection = (tradeId: number) => {
    setSelectedTradeIds(prev => {
      const newSet = new Set(prev)
      if (newSet.has(tradeId)) {
        newSet.delete(tradeId)
      } else {
        newSet.add(tradeId)
      }
      return newSet
    })
  }

  const selectAllOpenTrades = () => {
    if (!userTrades) return
    const openTradeIds = new Set<number>()
    userTrades.hedge_pairs.forEach(hp => {
      hp.trades.forEach(trade => {
        if (trade.status === 'OPEN') {
          openTradeIds.add(trade.id)
        }
      })
    })
    setSelectedTradeIds(openTradeIds)
  }

  const clearSelection = () => {
    setSelectedTradeIds(new Set())
  }

  const handleDeleteTrades = async () => {
    if (selectedTradeIds.size === 0) return
    if (!confirm(`Sei sicuro di voler eliminare ${selectedTradeIds.size} operazioni?`)) return
    
    setProcessingTrades(true)
    try {
      await api.delete('/admin/trades', {
        data: { trade_ids: Array.from(selectedTradeIds) }
      })
      toast.success(`Eliminate ${selectedTradeIds.size} operazioni`)
      setSelectedTradeIds(new Set())
      if (selectedUser) {
        loadUserTrades(selectedUser.user_id)
      }
    } catch (err: any) {
      toast.error('Errore: ' + (err.response?.data?.detail || err.message))
    } finally {
      setProcessingTrades(false)
    }
  }

  const handleMarkClosed = async () => {
    if (selectedTradeIds.size === 0) return
    
    setProcessingTrades(true)
    try {
      await api.post('/admin/trades/mark-closed', {
        trade_ids: Array.from(selectedTradeIds)
      })
      toast.success(`Marcate come CLOSED ${selectedTradeIds.size} operazioni`)
      setSelectedTradeIds(new Set())
      if (selectedUser) {
        loadUserTrades(selectedUser.user_id)
      }
    } catch (err: any) {
      toast.error('Errore: ' + (err.response?.data?.detail || err.message))
    } finally {
      setProcessingTrades(false)
    }
  }

  const handleCleanupTestTrades = async () => {
    if (!confirm('Questo marcherà come CLOSED tutte le operazioni di test vecchie (>7 giorni, senza SL/TP). Confermi?')) return
    
    setProcessingTrades(true)
    try {
      const { data } = await api.post('/admin/trades/cleanup-test-trades')
      toast.success(data.message)
      if (selectedUser) {
        loadUserTrades(selectedUser.user_id)
      }
    } catch (err: any) {
      toast.error('Errore: ' + (err.response?.data?.detail || err.message))
    } finally {
      setProcessingTrades(false)
    }
  }

  const formatCurrency = (value: number, currency: 'USD' | 'EUR') => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: currency
    }).format(value)
  }

  const getPaymentStatusBadge = (status: string) => {
    const variants: Record<string, { variant: 'default' | 'secondary' | 'destructive' | 'outline', label: string }> = {
      paid: { variant: 'default', label: 'Pagato' },
      unpaid: { variant: 'destructive', label: 'Non Pagato' },
      pending: { variant: 'secondary', label: 'In Attesa' }
    }
    const config = variants[status] || variants.pending
    return <Badge variant={config.variant}>{config.label}</Badge>
  }

  const tabs = [
    { id: 'dashboard' as const, label: '📊 Dashboard', icon: TrendingUp },
    { id: 'users' as const, label: '👥 Utenti', icon: Users },
    { id: 'snapshots' as const, label: '📈 Snapshots', icon: FileText },
    { id: 'payouts' as const, label: '💰 Payouts', icon: Wallet }
  ]

  return (
    <div className="space-y-6">
      <div className="flex space-x-2 border-b">
        {tabs.map(tab => {
          const Icon = tab.icon
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 border-b-2 transition-colors flex items-center gap-2 ${
                activeTab === tab.id 
                  ? 'border-primary text-primary font-medium' 
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
            </button>
          )
        })}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : (
        <>
          {/* DASHBOARD TAB */}
          {activeTab === 'dashboard' && kpi && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardDescription>Profitto Totale USD</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-green-500">
                      {formatCurrency(kpi.total_verified_profit_usd, 'USD')}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {formatCurrency(kpi.total_verified_profit_eur, 'EUR')}
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardDescription>Fee Piattaforma (10%)</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-blue-500">
                      {formatCurrency(kpi.total_platform_fees_eur, 'EUR')}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Da {kpi.users_with_profits} utenti
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardDescription>PROP Attivi</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">
                      {kpi.active_prop_accounts}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Accounts in gestione
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardDescription>PROP Bruciati</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-red-500">
                      {kpi.total_broken_props}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Equity ≤ 94%
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardDescription>Payouts Ricevuti</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">
                      {kpi.total_payouts_received}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {formatCurrency(kpi.total_payouts_amount_usd, 'USD')}
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardDescription>Transizioni Fase</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-purple-500">
                      {kpi.total_phase_transitions}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Challenge → Real
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardDescription>Fatture Non Pagate</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-orange-500">
                      {kpi.unpaid_invoices_count}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Richiede attenzione
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardDescription>Ciclo Billing</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="text-sm font-medium">
                      {new Date(kpi.cycle_start).toLocaleDateString('it-IT')}
                    </div>
                    <div className="text-sm font-medium">
                      {new Date(kpi.cycle_end).toLocaleDateString('it-IT')}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}

          {/* USERS TAB */}
          {activeTab === 'users' && !selectedUser && (
            <Card>
              <CardHeader>
                <CardTitle>Profitti per Utente</CardTitle>
                <CardDescription>Clicca su un utente per vedere le sue operazioni</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left p-2">Email</th>
                        <th className="text-right p-2">PROP Attivi</th>
                        <th className="text-right p-2">Profitto USD</th>
                        <th className="text-right p-2">Profitto EUR</th>
                        <th className="text-right p-2">Fee 10%</th>
                        <th className="text-center p-2">Stato</th>
                        <th className="text-right p-2">Ultimo Snapshot</th>
                        <th className="text-center p-2"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map(user => (
                        <tr 
                          key={user.user_id} 
                          className="border-b hover:bg-muted/50 cursor-pointer transition-colors"
                          onClick={() => handleUserClick(user)}
                        >
                          <td className="p-2 font-medium">{user.email}</td>
                          <td className="p-2 text-right">{user.active_props_count}</td>
                          <td className="p-2 text-right text-green-500 font-medium">
                            {formatCurrency(user.verified_profit_usd, 'USD')}
                          </td>
                          <td className="p-2 text-right text-green-500 font-medium">
                            {formatCurrency(user.verified_profit_eur, 'EUR')}
                          </td>
                          <td className="p-2 text-right text-blue-500 font-medium">
                            {formatCurrency(user.platform_fee_eur, 'EUR')}
                          </td>
                          <td className="p-2 text-center">
                            {getPaymentStatusBadge(user.payment_status)}
                          </td>
                          <td className="p-2 text-right text-muted-foreground">
                            {user.last_snapshot_date ? new Date(user.last_snapshot_date).toLocaleDateString('it-IT') : '-'}
                          </td>
                          <td className="p-2 text-center">
                            <ChevronRight className="w-4 h-4 text-muted-foreground" />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {users.length === 0 && (
                    <div className="text-center py-8 text-muted-foreground">
                      Nessun utente con profitti nel ciclo corrente
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* USER TRADES DETAIL VIEW */}
          {activeTab === 'users' && selectedUser && (
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <Button variant="ghost" size="sm" onClick={handleBackToUsers}>
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Torna a Utenti
                </Button>
                <div className="flex-1">
                  <h2 className="text-xl font-semibold">{selectedUser.email}</h2>
                  <p className="text-sm text-muted-foreground">
                    {selectedUser.active_props_count} PROP attivi | 
                    Profitto: {formatCurrency(selectedUser.verified_profit_usd, 'USD')}
                  </p>
                </div>
              </div>

              {loadingTrades ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-primary" />
                </div>
              ) : userTrades ? (
                <div className="space-y-6">
                  {/* Trade Management Actions */}
                  <Card className="bg-muted/30">
                    <CardContent className="py-4">
                      <div className="flex flex-wrap items-center gap-4">
                        <div className="flex items-center gap-2">
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={selectAllOpenTrades}
                            disabled={processingTrades}
                          >
                            Seleziona tutte OPEN
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={clearSelection}
                            disabled={selectedTradeIds.size === 0 || processingTrades}
                          >
                            Deseleziona ({selectedTradeIds.size})
                          </Button>
                        </div>
                        
                        <div className="h-6 w-px bg-border" />
                        
                        <div className="flex items-center gap-2">
                          <Button 
                            variant="secondary" 
                            size="sm" 
                            onClick={handleMarkClosed}
                            disabled={selectedTradeIds.size === 0 || processingTrades}
                          >
                            {processingTrades ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                            Marca CLOSED
                          </Button>
                          <Button 
                            variant="destructive" 
                            size="sm" 
                            onClick={handleDeleteTrades}
                            disabled={selectedTradeIds.size === 0 || processingTrades}
                          >
                            {processingTrades ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
                            Elimina
                          </Button>
                        </div>
                        
                        <div className="h-6 w-px bg-border" />
                        
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={handleCleanupTestTrades}
                          disabled={processingTrades}
                        >
                          <Wrench className="w-4 h-4 mr-2" />
                          Pulisci Test Vecchi
                        </Button>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Summary */}
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <Card>
                      <CardContent className="pt-4">
                        <p className="text-sm text-muted-foreground">Totale Operazioni</p>
                        <p className="text-2xl font-bold">{userTrades.total_trades}</p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-4">
                        <p className="text-sm text-muted-foreground">P&L PROP</p>
                        <p className={`text-2xl font-bold ${(userTrades.total_prop_pnl || 0) >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                          {formatCurrency(userTrades.total_prop_pnl || 0, 'USD')}
                        </p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-4">
                        <p className="text-sm text-muted-foreground">P&L Broker</p>
                        <p className={`text-2xl font-bold ${(userTrades.total_broker_pnl || 0) >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                          {formatCurrency(userTrades.total_broker_pnl || 0, 'USD')}
                        </p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-4">
                        <p className="text-sm text-muted-foreground">P&L Combinato</p>
                        <p className={`text-2xl font-bold ${userTrades.total_profit_loss >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                          {formatCurrency(userTrades.total_profit_loss, 'USD')}
                        </p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-4">
                        <p className="text-sm text-muted-foreground">Hedge Pairs</p>
                        <p className="text-2xl font-bold">{userTrades.hedge_pairs.length}</p>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Trades grouped by hedge pair */}
                  {userTrades.hedge_pairs.length === 0 ? (
                    <Card>
                      <CardContent className="py-12 text-center text-muted-foreground">
                        Nessuna operazione trovata per questo utente
                      </CardContent>
                    </Card>
                  ) : (
                    userTrades.hedge_pairs.map(hp => (
                      <Card key={hp.hedge_pair_id}>
                        <CardHeader className="pb-2">
                          <div className="flex items-center justify-between">
                            <div>
                              <CardTitle className="text-lg">
                                {hp.display_name || `${hp.prop_account_name} ↔ ${hp.broker_account_name}`}
                              </CardTitle>
                              <CardDescription>
                                PROP: {hp.prop_account_name} | Broker: {hp.broker_account_name}
                              </CardDescription>
                            </div>
                            <div className="text-right space-y-1">
                              <p className="text-sm text-muted-foreground">{hp.trades_count} operazioni</p>
                              <div className="flex gap-4 justify-end">
                                <div>
                                  <span className="text-xs text-muted-foreground">PROP: </span>
                                  <span className={`font-bold ${(hp.total_prop_pnl || 0) >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                                    {formatCurrency(hp.total_prop_pnl || 0, 'USD')}
                                  </span>
                                </div>
                                <div>
                                  <span className="text-xs text-muted-foreground">Broker: </span>
                                  <span className={`font-bold ${(hp.total_broker_pnl || 0) >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                                    {formatCurrency(hp.total_broker_pnl || 0, 'USD')}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent>
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="border-b">
                                  <th className="w-8 p-2"></th>
                                  <th className="text-left p-2">Data</th>
                                  <th className="text-left p-2">Account</th>
                                  <th className="text-center p-2">Tipo</th>
                                  <th className="text-right p-2">Volume</th>
                                  <th className="text-right p-2">Prezzo</th>
                                  <th className="text-right p-2">SL</th>
                                  <th className="text-right p-2">TP</th>
                                  <th className="text-center p-2">Stato</th>
                                  <th className="text-right p-2">P&L</th>
                                </tr>
                              </thead>
                              <tbody>
                                {hp.trades.map(trade => (
                                  <tr 
                                    key={trade.id} 
                                    className={`border-b hover:bg-muted/50 cursor-pointer ${selectedTradeIds.has(trade.id) ? 'bg-primary/10' : ''}`}
                                    onClick={() => toggleTradeSelection(trade.id)}
                                  >
                                    <td className="p-2">
                                      <input 
                                        type="checkbox" 
                                        checked={selectedTradeIds.has(trade.id)}
                                        onChange={() => toggleTradeSelection(trade.id)}
                                        onClick={(e) => e.stopPropagation()}
                                        className="w-4 h-4 rounded border-gray-600"
                                      />
                                    </td>
                                    <td className="p-2 text-muted-foreground">
                                      {new Date(trade.opened_at).toLocaleString('it-IT', { 
                                        day: '2-digit', 
                                        month: '2-digit', 
                                        year: '2-digit',
                                        hour: '2-digit',
                                        minute: '2-digit'
                                      })}
                                    </td>
                                    <td className="p-2">
                                      <Badge variant={trade.prop_account_id ? 'default' : 'secondary'}>
                                        {trade.prop_account_name || trade.broker_account_name}
                                      </Badge>
                                    </td>
                                    <td className="p-2 text-center">
                                      <Badge variant={trade.direction === 'BUY' ? 'default' : 'destructive'}>
                                        {trade.direction}
                                      </Badge>
                                    </td>
                                    <td className="p-2 text-right font-mono">{trade.volume.toFixed(2)}</td>
                                    <td className="p-2 text-right font-mono">{trade.open_price.toFixed(2)}</td>
                                    <td className="p-2 text-right font-mono text-red-400">
                                      {trade.stop_loss?.toFixed(2) || '-'}
                                    </td>
                                    <td className="p-2 text-right font-mono text-green-400">
                                      {trade.take_profit?.toFixed(2) || '-'}
                                    </td>
                                    <td className="p-2 text-center">
                                      <Badge variant={trade.status === 'CLOSED' ? 'secondary' : 'outline'}>
                                        {trade.status}
                                      </Badge>
                                    </td>
                                    <td className={`p-2 text-right font-bold ${trade.profit_loss >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                                      {formatCurrency(trade.profit_loss, 'USD')}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </div>
              ) : null}
            </div>
          )}

          {/* SNAPSHOTS TAB */}
          {activeTab === 'snapshots' && (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Snapshot Giornalieri</CardTitle>
                    <CardDescription>Equity, balance, payouts da MetaAPI</CardDescription>
                  </div>
                  <Button
                    variant={onlyBroken ? 'destructive' : 'outline'}
                    size="sm"
                    onClick={() => setOnlyBroken(!onlyBroken)}
                  >
                    {onlyBroken ? '🚨 Solo Bruciati' : 'Mostra Tutti'}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left p-2">Data</th>
                        <th className="text-left p-2">User</th>
                        <th className="text-left p-2">Account</th>
                        <th className="text-left p-2">Provider</th>
                        <th className="text-right p-2">Size</th>
                        <th className="text-right p-2">Equity</th>
                        <th className="text-right p-2">Equity %</th>
                        <th className="text-right p-2">Broker</th>
                        <th className="text-right p-2">Payouts</th>
                        <th className="text-center p-2">Fase</th>
                        <th className="text-center p-2">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {snapshots.map(snap => (
                        <tr 
                          key={snap.id} 
                          className={`border-b hover:bg-muted/50 ${snap.is_prop_broken ? 'bg-red-500/10' : ''}`}
                        >
                          <td className="p-2 text-muted-foreground">
                            {new Date(snap.snapshot_date).toLocaleDateString('it-IT')}
                          </td>
                          <td className="p-2 font-medium">{snap.email}</td>
                          <td className="p-2 text-sm">{snap.prop_account_name}</td>
                          <td className="p-2">{snap.provider}</td>
                          <td className="p-2 text-right">
                            {new Intl.NumberFormat('it-IT').format(snap.prop_size)}
                          </td>
                          <td className="p-2 text-right">{formatCurrency(snap.prop_equity, 'USD')}</td>
                          <td className={`p-2 text-right font-medium ${
                            snap.prop_equity_percentage >= 110 ? 'text-green-500' :
                            snap.prop_equity_percentage <= 94 ? 'text-red-500' :
                            'text-yellow-500'
                          }`}>
                            {snap.prop_equity_percentage.toFixed(1)}%
                          </td>
                          <td className="p-2 text-right">{formatCurrency(snap.broker_balance, 'USD')}</td>
                          <td className="p-2 text-right">
                            {formatCurrency(snap.total_payouts, 'USD')}
                            {snap.payout_count > 0 && (
                              <span className="text-xs text-muted-foreground ml-1">
                                ({snap.payout_count})
                              </span>
                            )}
                          </td>
                          <td className="p-2 text-center">
                            <Badge variant={snap.current_phase === 'real_pre_payout' || snap.current_phase === 'real_post_payout' ? 'default' : 'secondary'}>
                              {snap.current_phase}
                            </Badge>
                          </td>
                          <td className="p-2 text-center">
                            {snap.is_prop_broken && <Badge variant="destructive">Bruciato</Badge>}
                            {snap.phase_transition_detected && (
                              <Badge variant="outline" className="ml-1">
                                {snap.phase_transition_detected}
                              </Badge>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {snapshots.length === 0 && (
                    <div className="text-center py-8 text-muted-foreground">
                      {onlyBroken ? 'Nessun PROP bruciato' : 'Nessun snapshot disponibile'}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* PAYOUTS TAB */}
          {activeTab === 'payouts' && (
            <Card>
              <CardHeader>
                <CardTitle>Cronologia Payouts</CardTitle>
                <CardDescription>Tutti i payouts rilevati da MetaAPI</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left p-2">Data</th>
                        <th className="text-left p-2">User</th>
                        <th className="text-left p-2">Account</th>
                        <th className="text-right p-2">Importo</th>
                        <th className="text-center p-2">Numero</th>
                        <th className="text-center p-2">1° Payout</th>
                        <th className="text-center p-2">Costo Refunded</th>
                        <th className="text-left p-2">Deal ID</th>
                      </tr>
                    </thead>
                    <tbody>
                      {payouts.map(payout => (
                        <tr key={payout.id} className="border-b hover:bg-muted/50">
                          <td className="p-2 text-muted-foreground">
                            {new Date(payout.payout_date).toLocaleDateString('it-IT')}
                          </td>
                          <td className="p-2 font-medium">{payout.email}</td>
                          <td className="p-2 text-sm">{payout.prop_account_name}</td>
                          <td className="p-2 text-right text-green-500 font-medium">
                            {formatCurrency(payout.payout_amount, 'USD')}
                          </td>
                          <td className="p-2 text-center">
                            <Badge variant={payout.is_first_payout ? 'default' : 'secondary'}>
                              #{payout.payout_number}
                            </Badge>
                          </td>
                          <td className="p-2 text-center">
                            {payout.is_first_payout ? '✅' : '-'}
                          </td>
                          <td className="p-2 text-center">
                            {payout.cost_refunded ? '✅' : '-'}
                          </td>
                          <td className="p-2 text-muted-foreground font-mono text-xs">
                            {payout.metaapi_deal_id || '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {payouts.length === 0 && (
                    <div className="text-center py-8 text-muted-foreground">
                      Nessun payout rilevato
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  )
}
